import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowUpRight, Star } from "lucide-react";

export function KpiCard({ label, value, sub, icon: Icon, tone = "default", testId, to }) {
  // Tones only tint the border. We deliberately do NOT set a background
  // here because `bg-card` on the Card would otherwise be overridden by
  // the arbitrary utility, killing the frosted-glass surface. The glass
  // fill from index.css must always survive.
  const tones = {
    default: "",
    good:    "!border-[hsl(var(--ap-success)/0.55)] ring-1 ring-[hsl(var(--ap-success)/0.15)]",
    alert:   "!border-[hsl(var(--ap-warning)/0.65)] ring-1 ring-[hsl(var(--ap-warning)/0.15)]",
    danger:  "!border-[hsl(var(--destructive)/0.65)] ring-1 ring-[hsl(var(--destructive)/0.15)]",
  };
  const interactive = Boolean(to);
  const cls = `rounded-xl border bg-card shadow-sm ${tones[tone]} ${
    interactive
      ? "cursor-pointer transition-transform transition-shadow hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 group"
      : ""
  }`;
  const body = (
    <Card className={cls} data-testid={testId}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">{label}</CardTitle>
        <div className="flex items-center gap-1">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          {interactive && <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl sm:text-4xl font-serif tabular-nums leading-none" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
        {sub && <div className="mt-2 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
  if (!interactive) return body;
  return (
    <Link to={to} className="block focus-visible:outline-none" data-testid={testId ? `${testId}-link` : undefined} aria-label={typeof label === "string" ? `${label}. open details` : "Open details"}>
      {body}
    </Link>
  );
}

export function PoolPill({ pool, stars = 0 }) {
  const map = {
    Elite: "bg-[hsl(var(--ap-forest)/0.10)] text-[hsl(var(--ap-forest))] border-[hsl(var(--ap-forest)/0.25)]",
    Good: "bg-[hsl(var(--ap-saddle)/0.10)] text-[hsl(var(--ap-saddle))] border-[hsl(var(--ap-saddle)/0.25)]",
    "Average+": "bg-[hsl(var(--ap-info)/0.10)] text-[hsl(var(--ap-info))] border-[hsl(var(--ap-info)/0.25)]",
    "Below Average": "bg-[hsl(var(--ap-olive)/0.12)] text-[hsl(var(--ap-olive))] border-[hsl(var(--ap-olive)/0.25)]",
    Unsalvageable: "bg-[hsl(var(--ap-warning)/0.10)] text-[hsl(var(--ap-warning))] border-[hsl(var(--ap-warning)/0.30)]",
    // Legacy names kept so any cached / pre-migration data still gets coloured.
    Commercial: "bg-[hsl(var(--ap-saddle)/0.10)] text-[hsl(var(--ap-saddle))] border-[hsl(var(--ap-saddle)/0.25)]",
    Development: "bg-[hsl(var(--ap-info)/0.10)] text-[hsl(var(--ap-info))] border-[hsl(var(--ap-info)/0.25)]",
    Green: "bg-[hsl(var(--ap-olive)/0.12)] text-[hsl(var(--ap-olive))] border-[hsl(var(--ap-olive)/0.25)]",
    Review: "bg-[hsl(var(--ap-warning)/0.10)] text-[hsl(var(--ap-warning))] border-[hsl(var(--ap-warning)/0.30)]",
    Unrated: "bg-muted text-muted-foreground border-border",
  };
  const cls = map[pool] || map.Unrated;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`} data-testid="horse-pool-pill">
      {pool || "Unrated"}
      {stars > 0 && (
        <span className="ml-1 inline-flex items-center gap-[1px]" aria-label={`${stars} of 5 stars`}>
          {Array.from({ length: stars }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key -- decorative rating dots
            <Star key={`rating-${stars}-${i}`} className="h-3 w-3 fill-current" strokeWidth={1.5} />
          ))}
        </span>
      )}
    </span>
  );
}

export function StatusPill({ status }) {
  const map = {
    verified: "bg-[hsl(var(--ap-success)/0.10)] text-[hsl(var(--ap-success))] border-[hsl(var(--ap-success)/0.25)]",
    pending: "bg-[hsl(var(--ap-warning)/0.10)] text-[hsl(var(--ap-warning))] border-[hsl(var(--ap-warning)/0.30)]",
    paid: "bg-[hsl(var(--ap-info)/0.10)] text-[hsl(var(--ap-info))] border-[hsl(var(--ap-info)/0.25)]",
    operational: "bg-[hsl(var(--ap-success)/0.10)] text-[hsl(var(--ap-success))] border-[hsl(var(--ap-success)/0.25)]",
    maintenance: "bg-[hsl(var(--ap-warning)/0.10)] text-[hsl(var(--ap-warning))] border-[hsl(var(--ap-warning)/0.30)]",
    out_of_service: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.25)]",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${map[status] || "bg-muted text-muted-foreground border-border"}`} data-testid="status-pill">
      {status}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div>
        {eyebrow && <div className="ap-eyebrow mb-2" data-testid="page-header-eyebrow">{eyebrow}</div>}
        <h1 className="ap-title-display" data-testid="page-header-title">{title}</h1>
        {subtitle && <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function SectionTitle({ children }) {
  return <h2 className="text-sm font-medium tracking-tight mb-3">{children}</h2>;
}
